// AppCreationErrorModal Component
// Displays error messages when app creation fails due to limits or validation errors
// Used in both app section and main section for consistent error handling

import React from 'react';
import { RevoltModal, RevoltButton } from './';
import { envUtils } from '../core/utils';
import { useNavigationManager } from '../core';
import './AppCreationErrorModal.css';

export function AppCreationErrorModal({
  isOpen,
  onClose,
  errorCode,
  errorMessage,
  details = {}
}) {
  // Error type configurations
  const errorConfigs = {
    APP_LIMIT_EXCEEDED: {
      title: "App Limit Reached",
      icon: "fa-solid fa-layer-group",
      message: errorMessage || "You have reached your app limit. Please upgrade your plan or delete an app to create a new one.",
      hint: "Upgrade your plan to create more apps, or delete existing apps to free up space.",
      showUpgradeButton: true
    },
    STORAGE_LIMIT_EXCEEDED: {
      title: "Storage Limit Exceeded",
      icon: "fa-solid fa-hard-drive",
      message: errorMessage || "You don't have enough storage space to create this app. Please upgrade your plan or delete some files.",
      hint: "Upgrade your plan for more storage, or delete files from existing apps to free up space.",
      showUpgradeButton: true
    },
    STORAGE_LIMIT_EXCEEDED_NOT_OWNER: {
      title: "App Owner's Storage Limit Exceeded",
      icon: "fa-solid fa-hard-drive",
      message: errorMessage || "The app owner has run out of storage. Only they can upgrade their plan or free up space.",
      hint: "Contact the app owner to upgrade their plan or delete files from this app.",
      showUpgradeButton: false
    },
    BUNDLE_SIZE_LIMIT_EXCEEDED: {
      title: "Bundle Size Limit Exceeded",
      icon: "fa-solid fa-file-zipper",
      message: errorMessage || "The bundle size exceeds the maximum allowed size of 5 GB. Please reduce the number or size of files in your app.",
      hint: "Delete files you don't need or reduce file sizes so your app stays under 5 GB total.",
      showUpgradeButton: false
    },
    TEMPLATE_NOT_FOUND: {
      title: "Template Not Found",
      icon: "fa-solid fa-file-circle-question",
      message: errorMessage || "The selected template could not be found. Please try selecting a different template.",
      hint: "The template may have been removed or renamed. Try selecting a different template.",
      showUpgradeButton: false
    },
    VALIDATION_ERROR: {
      title: "Validation Error",
      icon: "fa-solid fa-triangle-exclamation",
      message: errorMessage || "There was an error validating your app creation request.",
      hint: "Please check your input and try again.",
      showUpgradeButton: false
    },
    INSUFFICIENT_CREDITS: {
      // Default (website/mobile): keeps existing copy via errorMessage/errorCode plumbing,
      // but we still hide the "details" bar for this specific error below.
      title: "Insufficient Credits",
      icon: "fa-solid fa-circle-exclamation",
      message:
        errorMessage ||
        "You do not have enough credits to run the automated app payload creator.",
      hint: "Upgrade your plan to generate apps automatically from the landing page.",
      showUpgradeButton: true
    },
    generic: {
      title: "App Creation Failed",
      icon: "fa-solid fa-circle-exclamation",
      message: errorMessage || "Failed to create app. Please try again.",
      hint: "If the problem persists, contact support.",
      showUpgradeButton: false
    }
  };

  // Storage limit: show different message when current user is not the app owner (admin/member syncing)
  const effectiveErrorCode =
    errorCode === "STORAGE_LIMIT_EXCEEDED" && details.isOwner === false
      ? "STORAGE_LIMIT_EXCEEDED_NOT_OWNER"
      : errorCode;

  const navigationManager = useNavigationManager();

  const isInsufficientCredits = effectiveErrorCode === "INSUFFICIENT_CREDITS";
  const isDesktopEnv = envUtils?.device?.isDesktopApp?.() === true || envUtils?.device?.getEnvironmentType?.() === "desktop";

  // Choose config first, then apply desktop-specific overrides for insufficient credits.
  let config = errorConfigs[effectiveErrorCode] || errorConfigs.generic;
  if (isInsufficientCredits && isDesktopEnv) {
    config = {
      title: "Local App Mode (Credits Not Required)",
      icon: "fa-solid fa-desktop",
      message:
        "You do not have enough credits to run the automated app payload creator.",
      hint:
        "On desktop, you can create the app manually from the Apps page (title + description). The automated creator is intentionally simple right now, so manual creation saves you from the credits issue without much extra work. We are actively working on upgrading this landing experience to be far more convenient.",
      showUpgradeButton: false
    };
  }

  const handleUpgrade = () => {
    // Navigate to pricing page
    window.location.href = '/pricing';
  };

  const handleGoToApps = () => {
    navigationManager.navigateTo('app');
  };

  const shouldShowDetails =
    details &&
    Object.keys(details).length > 0 &&
    !isInsufficientCredits; // For insufficient credits, the red "details" bar is confusing across environments.

  const desktopInsufficientCreditsParagraph = (
    <>
      <span className="app-creation-error-message__keyword--danger">
        Insufficient credits
      </span>{" "}
      to run the automated app payload creator.
      {" "}
      <span className="app-creation-error-message__keyword">
        On desktop
      </span>
      , this landing flow still depends on a{" "}
      <span className="app-creation-error-message__keyword">cloud model</span>{" "}
      for now. The important part: you can still create your app{" "}
      <span className="app-creation-error-message__keyword">
        manually
      </span>{" "}
      right away from the{" "}
      <span className="app-creation-error-message__keyword">
        Apps page
      </span>{" "}
      (enter your <span className="app-creation-error-message__keyword">title</span> and{" "}
      <span className="app-creation-error-message__keyword">description</span>).
      {" "}
      The automated creator is intentionally simple right now, so manual creation won’t cost you much time.
      {" "}
      <span className="app-creation-error-message__keyword">
        We are actively working
      </span>{" "}
      on upgrading this landing experience so it can support local-first app creation.
    </>
  );

  return (
    <RevoltModal
      isOpen={isOpen}
      onClose={onClose}
      title={config.title}
      containerClassName="app-creation-error-modal"
    >
      <div className="app-creation-error-content">
        <div className="app-creation-error-icon">
          <i className={config.icon}></i>
        </div>
        {isInsufficientCredits && isDesktopEnv ? (
          <>
            <p className="app-creation-error-hint app-creation-error-hint--desktop-context">
              You are seeing this because you are on desktop.
            </p>
            <p className="app-creation-error-message app-creation-error-message--insufficient-desktop">
              {desktopInsufficientCreditsParagraph}
            </p>
          </>
        ) : (
          <>
            <p className="app-creation-error-message">
              {isInsufficientCredits ? (
                <>
                  <strong>Insufficient credits:</strong> {config.message}
                </>
              ) : (
                config.message
              )}
            </p>
            {config.hint && (
              <p className="app-creation-error-hint">
                {config.hint}
              </p>
            )}
          </>
        )}
        {shouldShowDetails && (
          <div className="app-creation-error-details">
            {details.currentUsed !== undefined && details.allowedApps !== undefined && (
              <div className="app-creation-error-detail-item">
                <span className="detail-label">Current Apps:</span>
                <span className="detail-value">{details.currentUsed} / {details.allowedApps}</span>
              </div>
            )}
            {details.currentUsed !== undefined && details.allowedBytes !== undefined && (
              <div className="app-creation-error-detail-item">
                <span className="detail-label">Storage Used:</span>
                <span className="detail-value">
                  {details.currentUsed ? `${(details.currentUsed / 1024 / 1024).toFixed(2)} MB` : '0 MB'} /
                  {details.allowedBytes != null ? ` ${(details.allowedBytes / 1024 / 1024).toFixed(2)} MB` : ' Unlimited'}
                </span>
              </div>
            )}
            {details.requestedBytes !== undefined && (
              <div className="app-creation-error-detail-item">
                <span className="detail-label">Required:</span>
                <span className="detail-value">
                  {(details.requestedBytes / 1024).toFixed(2)} KB
                </span>
              </div>
            )}
            {details.bundleSizeGB !== undefined && details.maxBundleSizeGB !== undefined && (
              <div className="app-creation-error-detail-item">
                <span className="detail-label">Bundle Size:</span>
                <span className="detail-value">
                  {details.bundleSizeGB} GB / {details.maxBundleSizeGB} GB
                </span>
              </div>
            )}
          </div>
        )}
        <div className="app-creation-error-actions">
          <RevoltButton variant="outline" onClick={onClose}>
            Close
          </RevoltButton>
          {isInsufficientCredits && isDesktopEnv ? (
            <RevoltButton variant="accent" onClick={handleGoToApps}>
              Go to Apps Page
            </RevoltButton>
          ) : (
            config.showUpgradeButton && (
              <RevoltButton variant="accent" onClick={handleUpgrade}>
                Upgrade Plan
              </RevoltButton>
            )
          )}
        </div>
      </div>
    </RevoltModal>
  );
}
